import React from 'react';
import type { StatementLine, SupplierStatement } from '../../lib/supplierStatementApi';
import { TONE } from '../../components/ui/tokens';
import { formatDate } from '../../utils/dateFormat';
import { TYPE_LABEL, formatAmount, formatSide, periodLabel } from './statementDisplay';

/**
 * The statement itself: opening balance, every line with its running balance, closing balance.
 * The same table is what prints, so it carries its own flags in words, not just colour:
 * "estimated" on a date that is not the document's own, and a badge on money that was marked
 * paid with no payment record behind it.
 *
 * The link to the bill or voucher is its own last column, hidden in print.
 */
export interface StatementTableProps {
  statement: SupplierStatement;
  /** Where a line's bill or voucher is; the page decides how to get there. */
  onOpen: (path: string) => void;
}

/** Where a line points, or null when it has nothing to open. */
export function lineTarget(line: StatementLine): { path: string; label: string } | null {
  if (line.type === 'payment' && line.paymentId) {
    return { path: `/accounts/payments/${line.paymentId}`, label: 'Payment voucher' };
  }
  if (line.billId) return { path: `/accounts/${line.billId}`, label: 'Invoice' };
  return null;
}

const CELL = 'px-3 py-2 align-top';
const MONEY = `${CELL} text-right tabular-nums whitespace-nowrap`;

export const StatementTable: React.FC<StatementTableProps> = ({ statement, onOpen }) => {
  const currency = statement.currency || '';
  const { lines } = statement;

  return (
    <div className="statement-table-wrap relative overflow-x-auto">
      <table className="statement-table data-table data-table-grid w-full min-w-[46rem] text-left text-sm">
        <caption className="sr-only">
          Statement of account for {statement.supplierName} in {currency}, {periodLabel(statement.from, statement.to)}.
          Debits add to what we owe, credits reduce it.
        </caption>
        <thead className="border-b border-slate-200 bg-slate-50">
          <tr className="text-xs text-slate-600">
            <th scope="col" className={CELL}>
              Date
            </th>
            <th scope="col" className={CELL}>
              Type
            </th>
            <th scope="col" className={CELL}>
              Reference
            </th>
            <th scope="col" className={CELL}>
              Description
            </th>
            <th scope="col" className={`${CELL} text-right`}>
              Debit
            </th>
            <th scope="col" className={`${CELL} text-right`}>
              Credit
            </th>
            <th scope="col" className={`${CELL} text-right`}>
              Balance
            </th>
            <th scope="col" className={`${CELL} no-print`}>
              <span className="sr-only">Open</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr className="statement-opening border-b border-slate-200 bg-slate-50 font-semibold" data-testid="statement-opening">
            <th scope="row" colSpan={6} className={`${CELL} text-left`}>
              Opening balance, {formatDate(statement.from, '--')}
            </th>
            <td className={MONEY}>{formatAmount(statement.opening)}</td>
            <td className="no-print" />
          </tr>

          {lines.length === 0 ? (
            <tr data-testid="statement-empty">
              <td colSpan={8} className={`${CELL} py-6 text-center text-slate-600`}>
                Nothing was invoiced, paid or credited in this period. The opening balance carries through to the
                closing balance.
              </td>
            </tr>
          ) : null}

          {lines.map((line, index) => {
            const target = lineTarget(line);
            const flagged = line.type === 'adjustment';
            return (
              <tr
                key={`${line.type}-${line.paymentId || line.billId}-${index}`}
                className={`border-b border-slate-100 ${flagged ? 'statement-flagged bg-amber-50' : ''}`}
                data-line-type={line.type}
              >
                <td className={`${CELL} whitespace-nowrap`}>
                  {formatDate(line.date, '--')}
                  {line.dateEstimated ? (
                    <span
                      className="statement-estimated ml-1.5 rounded border border-amber-200 bg-amber-50 px-1 text-xs font-medium text-amber-800"
                      title="This date is not on the document. It is the day the record was made or last changed."
                    >
                      estimated
                    </span>
                  ) : null}
                </td>
                <td className={CELL}>
                  {flagged ? (
                    <span className={`inline-block rounded border px-1.5 text-xs font-semibold ${TONE.attention}`}>
                      {TYPE_LABEL[line.type]}
                    </span>
                  ) : (
                    TYPE_LABEL[line.type] || line.type
                  )}
                </td>
                <td className={`${CELL} break-words`}>{line.reference || ''}</td>
                <td className={`${CELL} break-words`}>{line.description}</td>
                <td className={MONEY}>{formatSide(line.debit)}</td>
                <td className={MONEY}>{formatSide(line.credit)}</td>
                <td className={`${MONEY} font-medium`}>{formatAmount(line.balance)}</td>
                <td className={`${CELL} no-print whitespace-nowrap`}>
                  {target ? (
                    <a
                      href={target.path}
                      onClick={(e) => {
                        // A plain click stays in the app; a modified click (new tab) is the browser's.
                        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                        e.preventDefault();
                        onOpen(target.path);
                      }}
                      aria-label={`Open ${target.label.toLowerCase()} ${line.reference || ''}`.trim()}
                      className="text-sm font-medium text-primary-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                    >
                      {target.label}
                    </a>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="statement-closing border-t-2 border-slate-300 bg-slate-50 font-semibold" data-testid="statement-closing">
            <th scope="row" colSpan={6} className={`${CELL} text-left`}>
              Closing balance, {formatDate(statement.to, '--')}
            </th>
            <td className={MONEY}>{formatAmount(statement.closing)}</td>
            <td className="no-print" />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};
